import axios from "axios";
import { useQuery } from "react-query";
import { nuclearn_api_url } from "src/App";
import { SearchResult } from "src/views/assistant/types/citations";

/**
 * Optional scope for a by-ids fetch. When `dialogue_id` is present the
 * backend validates the requested chunk ids against that dialogue's
 * citation allowlist (#1900).
 */
export type ChunkFetchScope = {
  dialogue_id?: number | null;
  message_id?: string | null;
};

/**
 * Fetch chunks by their IDs - API returns SearchResult format
 * @param chunkIds - Array of chunk IDs to fetch
 * @param scope - Optional dialogue/message scope for allowlist enforcement
 * @returns Promise<SearchResult[]>
 */
export const fetchChunksByIds = (
  chunkIds: number[],
  scope?: ChunkFetchScope,
) => {
  const body: {
    chunk_ids: number[];
    dialogue_id?: number;
    message_id?: string;
  } = { chunk_ids: chunkIds };
  if (scope?.dialogue_id != null) body.dialogue_id = scope.dialogue_id;
  if (scope?.message_id != null) body.message_id = scope.message_id;

  return axios
    .post<SearchResult[]>(`${nuclearn_api_url}/datasets/chunks/by-ids`, body)
    .then((response) => {
      return response.data;
    });
};

/**
 * React Query hook to fetch chunks by their IDs
 * @param chunkIds - Array of chunk IDs to fetch
 * @param options - React Query options
 * @param scope - Optional dialogue/message scope (part of the cache key so a
 *   scope change refetches)
 */
export const useChunksByIds = (
  chunkIds: number[],
  options?: {
    enabled?: boolean;
    staleTime?: number;
    cacheTime?: number;
  },
  scope?: ChunkFetchScope,
) => {
  return useQuery(
    [
      "chunks",
      "by-ids",
      [...chunkIds].sort(), // Sort a copy for a stable cache key (don't mutate caller state)
      scope?.dialogue_id ?? null,
      scope?.message_id ?? null,
    ],
    () => fetchChunksByIds(chunkIds, scope),
    {
      enabled: chunkIds.length > 0 && (options?.enabled ?? true),
      staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
      cacheTime: options?.cacheTime ?? 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};
